import { useEffect, useRef } from "react";
import type { RefObject } from "react";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Moves focus into `containerRef`'s first focusable descendant when `isOpen` becomes
 * true, and calls `onEscape` on Escape — without trapping Tab.
 *
 * For content that replaces the page in place (an inline swap, not a modal overlay),
 * a full focus trap would be wrong: there is no dimmed backdrop and nothing else on
 * screen to protect the user from tabbing into, so trapping Tab would make part of a
 * perfectly reachable page look broken. Moving focus in still matters — otherwise a
 * screen reader user's focus stays on the button that triggered the swap while the
 * content around it silently changes underneath (2.4.3 Focus Order — A).
 */
export function useFocusOnOpen<T extends HTMLElement>(
  containerRef: RefObject<T>,
  isOpen: boolean,
  onEscape: () => void,
): void {
  // Held in a ref so an inline callback does not tear down and re-attach the listener on
  // every render of the calling component.
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!isOpen) return;
    const container = containerRef.current;
    container?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onEscapeRef.current();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [containerRef, isOpen]);
}
